@file:OptIn(ExperimentalUuidApi::class)

package ai.wagemix.logic.jobdescriptions

import ai.wagemix.logic.entitledPlan
import ai.wagemix.logic.lapsedFlow
import ai.wagemix.data.touchesUser
import ai.wagemix.data.WMJobDescriptionSyncEvent
import ai.wagemix.data.WMQuotaExceededException
import ai.wagemix.net.models.countsAgainstUploadLimit
import ai.wagemix.net.models.WMUploadFailureReason
import ai.wagemix.data.WMUploadNotAcceptedException
import ai.wagemix.logic.durationMsOf
import ai.wagemix.logic.isForUser
import ai.wagemix.logic.jobdescriptions.WMJobDescriptionsPresenter.Action
import ai.wagemix.logic.jobdescriptions.WMJobDescriptionsPresenter.Event
import ai.wagemix.logic.jobdescriptions.WMJobDescriptionsPresenter.Prompt
import ai.wagemix.logic.sendWMMixReevaluationCompleted
import ai.wagemix.logic.sendWMMixReevaluationFailed
import ai.wagemix.logic.sendWMMixReevaluationRequested
import ai.wagemix.logic.session
import ai.wagemix.logic.uploadsFlow
import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.WMPlanRules
import ai.wagemix.logic.WMRoutes
import ai.wagemix.logic.withStartedAt
import ai.wagemix.net.models.firebase.WMSubscriptionType
import ai.wagemix.net.models.WMJobDescriptionFile
import ai.wagemix.net.models.WMUpload
import ai.wagemix.net.models.WMUploadFile
import ai.wagemix.net.models.WMUploadProgress
import com.fsryan.foundation.logic.FSPresenter
import com.fsryan.foundation.logic.FSPresenter.Event.NullableValueWrapper
import com.fsryan.foundation.logic.FSPrompt
import com.fsryan.foundation.logic.NullableValueWrapper
import com.fsryan.foundation.logic.presenterInstanceId
import com.fsryan.foundation.logic.prompting.defaultHandlePromptResponse
import com.fsryan.foundation.logic.prompting.FSBasePromptingPresenter
import com.fsryan.foundation.logic.prompting.FSPromptingPresenter
import com.fsryan.foundation.models.FSPropertyContainer
import com.fsryan.foundation.util.log.FSLog
import kotlin.jvm.JvmInline
import kotlin.time.Clock
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.onStart

data class WMUploadQuota(val used: Int, val quota: Int?, val plan: WMSubscriptionType) {
    val exhausted: Boolean get() = quota != null && used >= quota
}

enum class WMFileProblem { UnsupportedType, TooLarge, Empty }

/**
 * Job descriptions: the dropzone and URL form with their validation and quota, the upload rows
 * with status, opening the resulting mix, previewing and downloading the file, and re-evaluation.
 */
interface WMJobDescriptionsPresenter<P: Prompt, out E: Event<P>, in A: Action>: FSPromptingPresenter<P, E, A> {

    interface Action: FSPromptingPresenter.Action {
        sealed interface Base: Action

        @JvmInline
        value class ComposedSuperAction internal constructor(val wrapped: FSPromptingPresenter.Action.Base): Base

        @ConsistentCopyVisibility
        data class UploadFile internal constructor(val file: WMUploadFile, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<UploadFile> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class UploadUrl internal constructor(val url: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<UploadUrl> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class OpenMix internal constructor(val mixId: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<OpenMix> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class RequestFilePreview internal constructor(val mixId: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<RequestFilePreview> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class RequestReevaluate internal constructor(val mixId: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<RequestReevaluate> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        /** Run a failed upload's pipeline again on the file it already uploaded. */
        @ConsistentCopyVisibility
        data class RequestRetryUpload internal constructor(val uploadId: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<RequestRetryUpload> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        /** Delete a failed upload the user no longer wants (job-description-intake-plan rule 8); asks first. */
        @ConsistentCopyVisibility
        data class RequestDeleteUpload internal constructor(val uploadId: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<RequestDeleteUpload> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        interface Factory<P: Prompt, out A: Action>: FSPromptingPresenter.Action.Factory<P, A> {
            fun createUploadFile(file: WMUploadFile, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createUploadUrl(url: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createOpenMix(mixId: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createRequestFilePreview(mixId: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createRequestReevaluate(mixId: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createRequestRetryUpload(uploadId: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createRequestDeleteUpload(uploadId: String, extendedProperties: Map<String, Any> = emptyMap()): A
        }

        companion object {
            fun <P: Prompt> baseFactory(): Factory<P, Base> = object: Factory<P, Base> {
                private val base = FSPromptingPresenter.Action.baseFactory<P>()
                override fun createUserRespondedToPrompt(response: FSPrompt.Response<P>, extendedProperties: Map<String, Any>): Base = ComposedSuperAction(base.createUserRespondedToPrompt(response, extendedProperties))
                override fun createUploadFile(file: WMUploadFile, extendedProperties: Map<String, Any>): Base = UploadFile(file, extendedProperties)
                override fun createUploadUrl(url: String, extendedProperties: Map<String, Any>): Base = UploadUrl(url, extendedProperties)
                override fun createOpenMix(mixId: String, extendedProperties: Map<String, Any>): Base = OpenMix(mixId, extendedProperties)
                override fun createRequestFilePreview(mixId: String, extendedProperties: Map<String, Any>): Base = RequestFilePreview(mixId, extendedProperties)
                override fun createRequestReevaluate(mixId: String, extendedProperties: Map<String, Any>): Base = RequestReevaluate(mixId, extendedProperties)
                override fun createRequestRetryUpload(uploadId: String, extendedProperties: Map<String, Any>): Base = RequestRetryUpload(uploadId, extendedProperties)
                override fun createRequestDeleteUpload(uploadId: String, extendedProperties: Map<String, Any>): Base = RequestDeleteUpload(uploadId, extendedProperties)
            }
        }
    }

    interface Event<out P: Prompt>: FSPromptingPresenter.Event<P> {
        val uploadsUpdate: List<WMUpload>?
        val quotaUpdate: WMUploadQuota?
        /** Progress of the upload in flight, or a wrapped `null` once it finished. */
        val activeUploadUpdate: NullableValueWrapper<WMUploadProgress?>?
        val navigationUpdate: WMNavigationRequest?
        /**
         * Whether the account has lapsed: the screen shows the renewal reminder instead of its content, and the content
         * comes back on renewal (planning/paid-feature-enforcement-plan.md W7). `null` means no change.
         */
        val lapsedUpdate: Boolean?

        @ConsistentCopyVisibility
        data class Base<out P: Prompt> internal constructor(
            private val base: FSPromptingPresenter.Event.Base<P>,
            override val uploadsUpdate: List<WMUpload>? = null,
            override val quotaUpdate: WMUploadQuota? = null,
            override val activeUploadUpdate: NullableValueWrapper<WMUploadProgress?>? = null,
            override val navigationUpdate: WMNavigationRequest? = null,
            override val lapsedUpdate: Boolean? = null
        ): Event<P>, FSPropertyContainer<Base<P>>, FSPromptingPresenter.Event<P> by base {
            override val extendedProperties: Map<String, Any> get() = base.extendedProperties
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(base = base.replaceExtendedProperties(properties))
        }

        interface Factory<P: Prompt, out E: Event<P>>: FSPromptingPresenter.Event.Factory<P, E> {
            fun createUploadsUpdate(uploads: List<WMUpload>): E
            fun createQuotaUpdate(quota: WMUploadQuota): E
            fun createActiveUploadUpdate(progress: WMUploadProgress?): E
            fun createNavigation(request: WMNavigationRequest): E
            fun createLapsedUpdate(lapsed: Boolean): E
        }

        companion object {
            fun <P: Prompt> baseFactory(): Factory<P, Base<P>> = object: Factory<P, Base<P>> {
                private val base = FSPromptingPresenter.Event.baseFactory<P>()
                override fun createPromptChange(prompt: P, loading: Boolean?, extendedProperties: Map<String, Any>) = Base(base.createPromptChange(prompt, loading, extendedProperties))
                override fun createLoadingChange(loading: Boolean, extendedProperties: Map<String, Any>) = Base(base.createLoadingChange(loading, extendedProperties))
                override fun none() = Base(base.none())
                override fun createUploadsUpdate(uploads: List<WMUpload>) = Base(base.none(), uploadsUpdate = uploads)
                override fun createQuotaUpdate(quota: WMUploadQuota) = Base(base.none(), quotaUpdate = quota)
                override fun createActiveUploadUpdate(progress: WMUploadProgress?) = Base(base.none(), activeUploadUpdate = NullableValueWrapper(progress))
                override fun createNavigation(request: WMNavigationRequest) = Base(base.none(), navigationUpdate = request)
                override fun createLapsedUpdate(lapsed: Boolean) = Base(base.none(), lapsedUpdate = lapsed)
            }
        }
    }

    interface Prompt: FSPromptingPresenter.Prompt {
        sealed interface Base: Prompt

        @JvmInline
        value class ComposedSuperPrompt internal constructor(val wrapped: FSPromptingPresenter.Prompt.Base): Base

        @ConsistentCopyVisibility
        data class UploadLimitReached internal constructor(val quota: WMUploadQuota): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = null
            override val titleOverride: String? = null
            enum class Selection { Dismiss, ViewPlans }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        @ConsistentCopyVisibility
        data class InvalidFile internal constructor(val problem: WMFileProblem, val fileName: String): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = null
            override val titleOverride: String? = null
        }

        @ConsistentCopyVisibility
        data class InvalidUrl internal constructor(val problem: WMUrlProblem): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = null
            override val titleOverride: String? = null
        }

        /** The pipeline reported a failure; [message] is the server's text, when it sent one. */
        @ConsistentCopyVisibility
        data class UploadFailed internal constructor(val message: String?): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = message
            override val titleOverride: String? = null
        }

        /**
         * The upload produced no mix for a known [reason]; [explanation] is the API's (for a document that is not a job
         * description, the AI's). The dialog adds the remedy for the upload's source ([fromUrl]: a posting URL).
         * The upload did not count against the plan's limit.
         */
        @ConsistentCopyVisibility
        data class UploadNotAccepted internal constructor(
            val reason: WMUploadFailureReason,
            val fromUrl: Boolean,
            val explanation: String
        ): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = null
            override val titleOverride: String? = null
        }

        /** The job description behind a mix, shown before the user downloads it. */
        @ConsistentCopyVisibility
        data class PreviewFile internal constructor(val file: WMJobDescriptionFile): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = null
            override val titleOverride: String? = null
            enum class Selection { Close, Download }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        /** The uploaded file behind a mix is no longer stored, so there is nothing to show. */
        data object FileUnavailable: Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = null
            override val titleOverride: String? = null
        }

        /** Deleting a failed upload removes its file for good: the user confirms first. */
        @ConsistentCopyVisibility
        data class ConfirmDeleteUpload internal constructor(val uploadId: String, val fileName: String): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = null
            override val titleOverride: String? = null
            enum class Selection { Cancel, Delete }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        /** Deleting a failed upload did not work; it is still there. */
        @ConsistentCopyVisibility
        data class DeleteUploadFailed internal constructor(val message: String?): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = message
            override val titleOverride: String? = null
        }

        @ConsistentCopyVisibility
        data class ConfirmReevaluate internal constructor(val mixId: String, val fileName: String): Base, FSPrompt.Displayed {
            override val forceInteraction = true
            override val messageOverride: String? = null
            override val titleOverride: String? = null
            enum class Selection { Cancel, Reevaluate }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        interface Factory<out P: Prompt>: FSPromptingPresenter.Prompt.Factory<P> {
            fun uploadLimitReached(quota: WMUploadQuota): P
            fun invalidFile(problem: WMFileProblem, fileName: String): P
            fun invalidUrl(problem: WMUrlProblem): P
            fun uploadFailed(message: String?): P
            fun uploadNotAccepted(reason: WMUploadFailureReason, fromUrl: Boolean, explanation: String): P
            fun confirmReevaluate(mixId: String, fileName: String): P
            fun confirmDeleteUpload(uploadId: String, fileName: String): P
            fun deleteUploadFailed(message: String?): P
            fun previewFile(file: WMJobDescriptionFile): P
            fun fileUnavailable(): P
        }

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {
                override fun createBase(wrapped: FSPrompt.Base): Base = ComposedSuperPrompt(FSPromptingPresenter.Prompt.baseFactory().createBase(wrapped))
                override fun uploadLimitReached(quota: WMUploadQuota): Base = UploadLimitReached(quota)
                override fun invalidFile(problem: WMFileProblem, fileName: String): Base = InvalidFile(problem, fileName)
                override fun invalidUrl(problem: WMUrlProblem): Base = InvalidUrl(problem)
                override fun uploadFailed(message: String?): Base = UploadFailed(message)
                override fun uploadNotAccepted(
                    reason: WMUploadFailureReason,
                    fromUrl: Boolean,
                    explanation: String
                ): Base {
                    return UploadNotAccepted(reason, fromUrl, explanation)
                }
                override fun confirmReevaluate(mixId: String, fileName: String): Base = ConfirmReevaluate(mixId, fileName)
                override fun confirmDeleteUpload(uploadId: String, fileName: String): Base = ConfirmDeleteUpload(uploadId, fileName)
                override fun deleteUploadFailed(message: String?): Base = DeleteUploadFailed(message)
                override fun previewFile(file: WMJobDescriptionFile): Base = PreviewFile(file)
                override fun fileUnavailable(): Base = FileUnavailable
            }
        }
    }

    fun userDroppedFile(file: WMUploadFile, extendedProperties: Map<String, Any> = emptyMap())
    fun userSubmittedUrl(url: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedOpenMix(mixId: String, extendedProperties: Map<String, Any> = emptyMap())
    /** Shows the job description behind [mixId] in a preview the user can download it from. */
    fun userRequestedFilePreview(mixId: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedReevaluate(mixId: String, extendedProperties: Map<String, Any> = emptyMap())
    /** Runs the failed upload [uploadId] again. */
    fun userRequestedRetryUpload(uploadId: String, extendedProperties: Map<String, Any> = emptyMap())
    /** Deletes the failed upload [uploadId] after the user confirms. */
    fun userRequestedDeleteUpload(uploadId: String, extendedProperties: Map<String, Any> = emptyMap())

    companion object {
        const val MAX_FILE_BYTES = 25L * 1024 * 1024
        val ACCEPTED_CONTENT_TYPES = setOf("application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document")
        val ACCEPTED_EXTENSIONS = setOf("pdf", "docx")
        const val SCREEN = "jobDescriptions"
        const val PARAM_ACTIVE_UPLOAD = "activeUploadId"
    }
}

abstract class WMBaseJobDescriptionsPresenter<
    P: Prompt,
    E: Event<P>,
    A: Action,
    EF: Event.Factory<P, E>,
    AF: Action.Factory<P, A>,
    PF: Prompt.Factory<P>,
    G: WMJobDescriptionsDataGateway
>(
    protected val dataGateway: G,
    eventFactory: EF,
    actionFactory: AF,
    promptFactory: PF,
    cancelViewScopeOnUnready: Boolean,
    instanceId: String,
    /** Times the pipeline runs whose duration is reported. */
    protected val clock: Clock = Clock.System,
    handlePromptResponse: suspend (FSPrompt.Response<P>, suspend E.() -> Unit) -> Unit = { response, emit ->
        context(eventFactory, promptFactory, dataGateway) {
            // The syncs a response starts carry this presenter's id and their start time, so their events come back.
            val presenterContext = mapOf<String, Any>(FSPresenter.SYNC_EVENT_KEY_INSTANCE_ID to instanceId)
                .withStartedAt(clock)
            defaultJobDescriptionsHandlePromptResponse(response, presenterContext, emit)
        }
    }
): FSBasePromptingPresenter<P, E, A, EF, AF, PF>(cancelViewScopeOnUnready, instanceId, eventFactory, actionFactory, promptFactory, handlePromptResponse),
    WMJobDescriptionsPresenter<P, E, A> {

    override fun userDroppedFile(file: WMUploadFile, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createUploadFile(file, extendedProperties))
    override fun userSubmittedUrl(url: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createUploadUrl(url, extendedProperties))
    override fun userRequestedOpenMix(mixId: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createOpenMix(mixId, extendedProperties))
    override fun userRequestedFilePreview(mixId: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createRequestFilePreview(mixId, extendedProperties))
    override fun userRequestedReevaluate(mixId: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createRequestReevaluate(mixId, extendedProperties))
    override fun userRequestedRetryUpload(uploadId: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createRequestRetryUpload(uploadId, extendedProperties))
    override fun userRequestedDeleteUpload(uploadId: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createRequestDeleteUpload(uploadId, extendedProperties))

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun registerSubscriptions() {
        dataGateway.lapsedFlow()
            .onEach { lapsed -> eventFactory.createLapsedUpdate(lapsed).emit() }
            .launchInViewScope()

        val uidFlow = dataGateway.cacheUpdateFlow
            .filter { it.touchesUser() }
            .map { dataGateway.session()?.uid }
            .onStart { emit(dataGateway.session()?.uid) }
            .distinctUntilChanged()

        uidFlow
            .flatMapLatest { uid -> dataGateway.uploadsFlow(uid) }
            .onEach { eventFactory.createUploadsUpdate(it).emit() }
            .launchInViewScope()

        // Only uploads that produced a mix, or are being analysed, count (planning/job-description-intake-plan.md §4).
        uidFlow
            .flatMapLatest { uid -> dataGateway.uploadsFlow(uid).map { uploads -> usedUploads(uploads) } }
            .onEach { used -> eventFactory.createQuotaUpdate(quotaOf(used)).emit() }
            .launchInViewScope()

        // The plan is part of the user slice; any change to it re-derives the quota.
        dataGateway.cacheUpdateFlow
            .filter { it.touchesUser() }
            .onEach { eventFactory.createQuotaUpdate(currentQuota()).emit() }
            .launchInViewScope()

        // Progress of the upload in flight (keyed by the id recorded in the screen parameters).
        uidFlow
            .flatMapLatest { uid -> dataGateway.contextUpdateFlow.filter { it.isForUser(uid) } }
            .onEach { update ->
                val activeId = update.after.screenParams(WMJobDescriptionsPresenter.SCREEN)[WMJobDescriptionsPresenter.PARAM_ACTIVE_UPLOAD]
                if (activeId != null && update.uploadProgressChanged(activeId)) {
                    eventFactory.createActiveUploadUpdate(update.after.uploadProgress(activeId)).emit()
                }
            }
            .launchInViewScope()

        dataGateway.jobDescriptionSyncEventFlow
            .filter { it.presenterInstanceId() == instanceId }
            .onEach { handleSyncEvent(it) }
            .launchInViewScope()
    }

    private suspend fun handleSyncEvent(event: WMJobDescriptionSyncEvent<*>) {
        when (event) {
            is WMJobDescriptionSyncEvent.UploadFile.Initial -> eventFactory.createLoadingChange(true).emit()
            is WMJobDescriptionSyncEvent.UploadUrl.Initial -> eventFactory.createLoadingChange(true).emit()
            is WMJobDescriptionSyncEvent.Reevaluate.Initial -> eventFactory.createLoadingChange(true).emit()
            is WMJobDescriptionSyncEvent.UploadFile.Success -> {
                logAnalyzed(event.uid, event.eventStreamId, event.durationMsOf(clock))
                pipelineFinished(event.uid, event.eventStreamId)
            }
            is WMJobDescriptionSyncEvent.UploadUrl.Success -> {
                logAnalyzed(event.uid, event.eventStreamId, event.durationMsOf(clock))
                pipelineFinished(event.uid, event.eventStreamId)
            }
            is WMJobDescriptionSyncEvent.Reevaluate.Success -> {
                FSLog.sendWMMixReevaluationCompleted(mixId = event.mixId, durationMs = event.durationMsOf(clock))
                pipelineFinished(event.uid, event.eventStreamId)
            }
            is WMJobDescriptionSyncEvent.UploadFile.Failure -> {
                logUploadFailed(event.error)
                pipelineFailed(event.uid, event.error, fromUrl = false)
            }
            is WMJobDescriptionSyncEvent.UploadUrl.Failure -> {
                logUploadFailed(event.error)
                pipelineFailed(event.uid, event.error, fromUrl = true)
            }
            is WMJobDescriptionSyncEvent.Reevaluate.Failure -> {
                FSLog.sendWMMixReevaluationFailed(mixId = event.mixId, error = event.error)
                pipelineFailed(event.uid, event.error, fromUrl = false)
            }
            is WMJobDescriptionSyncEvent.RetryUpload.Failure -> {
                // The upload stays failed; the prompt says why the retry could not start: the plan's uploads are used
                // (plan rule 7), or something else (e.g. the file is gone).
                val prompt = when (event.error) {
                    is WMQuotaExceededException -> promptFactory.uploadLimitReached(currentQuota())
                    else -> promptFactory.uploadFailed(event.error.message)
                }
                eventFactory.createPromptChange(prompt).emit()
            }
            is WMJobDescriptionSyncEvent.DeleteUpload.Failure -> {
                eventFactory.createPromptChange(promptFactory.deleteUploadFailed(event.error.message)).emit()
            }
            is WMJobDescriptionSyncEvent.FetchFileUrls.Success -> {
                // The links are fresh; a mix the response left out has no stored file any more.
                val files = dataGateway.retrieveUserContext(event.uid).jobDescriptionFiles()
                val file = event.mixIds.firstOrNull()?.let { files[it] }
                val prompt = if (file != null) promptFactory.previewFile(file) else promptFactory.fileUnavailable()
                eventFactory.createPromptChange(prompt, loading = false).emit()
            }
            is WMJobDescriptionSyncEvent.FetchFileUrls.Failure -> {
                eventFactory.createPromptChange(promptFactory.networkError(), loading = false).emit()
            }
            else -> Unit
        }
    }

    /** `job_description_analyzed` once the progress entry names the mix the pipeline produced. */
    private suspend fun logAnalyzed(uid: String, eventStreamId: String, durationMs: Long?) {
        val context = dataGateway.retrieveUserContext(uid)
        val mixId = context.uploadProgress(eventStreamId)?.mixId ?: return
        val occupationCount = context.storedMixes()?.firstOrNull { it.id == mixId }?.occupations?.size
        FSLog.sendWMJobDescriptionAnalyzed(mixId = mixId, durationMs = durationMs, occupationCount = occupationCount)
    }

    private suspend fun logUploadFailed(error: Throwable) {
        when (error) {
            is WMQuotaExceededException -> {
                FSLog.sendWMJobDescriptionFailed(reason = WMJobDescriptionPipelineFailure.QuotaExceeded, error = error)
                FSLog.sendWMUploadQuotaReached(plan = currentQuota().plan)
            }
            is WMUploadNotAcceptedException -> FSLog.sendWMJobDescriptionFailed(reason = error.reason, error = error)
            else -> {
                FSLog.sendWMJobDescriptionFailed(reason = WMJobDescriptionPipelineFailure.PipelineFailed, error = error)
            }
        }
    }

    /** The pipeline ended well: the progress entry says which mix it produced. */
    private suspend fun pipelineFinished(uid: String, eventStreamId: String) {
        eventFactory.createLoadingChange(false).emit()
        val mixId = dataGateway.retrieveUserContext(uid).uploadProgress(eventStreamId)?.mixId
        dataGateway.deleteScreenParams(uid, WMJobDescriptionsPresenter.SCREEN)
        eventFactory.createActiveUploadUpdate(null).emit()
        mixId?.let { eventFactory.createNavigation(WMNavigationRequest.Route(WMRoutes.mix(it))).emit() }
    }

    private suspend fun pipelineFailed(uid: String, error: Throwable, fromUrl: Boolean) {
        dataGateway.deleteScreenParams(uid, WMJobDescriptionsPresenter.SCREEN)
        eventFactory.createActiveUploadUpdate(null).emit()
        val prompt = when (error) {
            is WMQuotaExceededException -> promptFactory.uploadLimitReached(currentQuota())
            is WMUploadNotAcceptedException -> {
                promptFactory.uploadNotAccepted(error.reason, fromUrl, error.message.orEmpty())
            }
            else -> promptFactory.uploadFailed(error.message)
        }
        eventFactory.createPromptChange(prompt, loading = false).emit()
    }

    override suspend fun sendInitialEvents(isFirstOnReadyCall: Boolean) {
        val context = dataGateway.retrieveUserContext(dataGateway.session()?.uid)
        val activeId = context.screenParams(WMJobDescriptionsPresenter.SCREEN)[WMJobDescriptionsPresenter.PARAM_ACTIVE_UPLOAD]
        eventFactory.createActiveUploadUpdate(activeId?.let { context.uploadProgress(it) }).emit()
    }

    override fun extractBasePromptingPresenterAction(action: A): FSPromptingPresenter.Action.Base? = (action as? Action.ComposedSuperAction)?.wrapped

    @Suppress("UNCHECKED_CAST")
    override fun FSPrompt.Response<*>.extract(): FSPrompt.Response<P> = this as FSPrompt.Response<P>

    override fun handleNonBasePromptingPresenterAction(action: A) {
        when (action) {
            is Action.UploadFile -> launch { handleUploadFile(action) }
            is Action.UploadUrl -> launch { handleUploadUrl(action) }
            is Action.OpenMix -> launch { eventFactory.createNavigation(WMNavigationRequest.Route(WMRoutes.mix(action.mixId))).emit() }
            is Action.RequestFilePreview -> launch {
                // Always new links: ones fetched earlier may have expired (they last three hours).
                val uid = dataGateway.session()?.uid ?: return@launch
                eventFactory.createLoadingChange(true).emit()
                dataGateway.fetchFileUrls(uid, setOf(action.mixId), createPresenterContext(action))
            }
            is Action.RequestRetryUpload -> launch {
                val uid = dataGateway.session()?.uid ?: return@launch
                dataGateway.retryUpload(uid, action.uploadId, createPresenterContext(action))
            }
            is Action.RequestDeleteUpload -> launch {
                val uid = dataGateway.session()?.uid ?: return@launch
                val upload = dataGateway.retrieveUserContext(uid).uploads()?.firstOrNull { it.id == action.uploadId }
                val name = upload?.sourceUrl ?: upload?.originalFileName ?: ""
                eventFactory.createPromptChange(promptFactory.confirmDeleteUpload(action.uploadId, name)).emit()
            }
            is Action.RequestReevaluate -> launch {
                val uid = dataGateway.session()?.uid ?: return@launch
                val upload = dataGateway.retrieveUserContext(uid).uploads()?.firstOrNull { it.mixId == action.mixId }
                eventFactory.createPromptChange(promptFactory.confirmReevaluate(action.mixId, upload?.originalFileName ?: "")).emit()
            }
            else -> logUnknownActionType(action)
        }
    }

    private suspend fun handleUploadFile(action: Action.UploadFile) {
        val uid = dataGateway.session()?.uid ?: return
        val quota = currentQuota()
        if (quota.exhausted) {
            FSLog.sendWMUploadQuotaReached(plan = quota.plan)
            return eventFactory.createPromptChange(promptFactory.uploadLimitReached(quota)).emit()
        }
        fileProblemOf(action.file)?.let { problem ->
            FSLog.sendWMJobDescriptionFailed(reason = problem)
            return eventFactory.createPromptChange(promptFactory.invalidFile(problem, action.file.name)).emit()
        }
        val id = Uuid.random().toString()
        dataGateway.upsertScreenParams(uid, WMJobDescriptionsPresenter.SCREEN, mapOf(WMJobDescriptionsPresenter.PARAM_ACTIVE_UPLOAD to id))
        FSLog.sendWMJobDescriptionUploadStarted(
            source = WMJobDescriptionSource.File,
            fileType = action.file.name.substringAfterLast('.', "").lowercase().ifEmpty { null },
            uploadsUsed = quota.used,
            uploadQuota = quota.quota,
        )
        val context = createPresenterContext(action).withStartedAt(clock)
        dataGateway.uploadJobDescription(uid, id, action.file, context)
    }

    private suspend fun handleUploadUrl(action: Action.UploadUrl) {
        val uid = dataGateway.session()?.uid ?: return
        val quota = currentQuota()
        if (quota.exhausted) {
            FSLog.sendWMUploadQuotaReached(plan = quota.plan)
            return eventFactory.createPromptChange(promptFactory.uploadLimitReached(quota)).emit()
        }
        WMJobBoards.problemOf(action.url)?.let { problem ->
            FSLog.sendWMJobDescriptionFailed(reason = problem)
            return eventFactory.createPromptChange(promptFactory.invalidUrl(problem)).emit()
        }
        val id = Uuid.random().toString()
        dataGateway.upsertScreenParams(uid, WMJobDescriptionsPresenter.SCREEN, mapOf(WMJobDescriptionsPresenter.PARAM_ACTIVE_UPLOAD to id))
        FSLog.sendWMJobDescriptionUploadStarted(
            source = WMJobDescriptionSource.Url,
            fileType = null,
            uploadsUsed = quota.used,
            uploadQuota = quota.quota,
        )
        val context = createPresenterContext(action).withStartedAt(clock)
        dataGateway.uploadJobDescriptionUrl(uid, id, action.url.trim(), context)
    }

    private suspend fun currentQuota(): WMUploadQuota {
        val uid = dataGateway.session()?.uid ?: return quotaOf(0)
        return quotaOf(usedUploads(dataGateway.retrieveUserContext(uid).uploads() ?: emptyList()))
    }

    private fun usedUploads(uploads: List<WMUpload>): Int = uploads.count { it.countsAgainstUploadLimit(clock.now()) }

    private suspend fun quotaOf(used: Int): WMUploadQuota {
        val plan = dataGateway.entitledPlan()
        return WMUploadQuota(used = used, quota = WMPlanRules.uploadQuota(plan), plan = plan)
    }

    protected open fun fileProblemOf(file: WMUploadFile): WMFileProblem? {
        val extension = file.name.substringAfterLast('.', "").lowercase()
        return when {
            file.bytes.isEmpty() -> WMFileProblem.Empty
            file.bytes.size > WMJobDescriptionsPresenter.MAX_FILE_BYTES -> WMFileProblem.TooLarge
            file.contentType !in WMJobDescriptionsPresenter.ACCEPTED_CONTENT_TYPES && extension !in WMJobDescriptionsPresenter.ACCEPTED_EXTENSIONS -> WMFileProblem.UnsupportedType
            else -> null
        }
    }
}

context(eventFactory: EF, promptFactory: PF, dataGateway: G)
suspend fun <
    P: Prompt,
    E: Event<P>,
    EF: Event.Factory<P, E>,
    PF: Prompt.Factory<P>,
    G: WMJobDescriptionsDataGateway
> defaultJobDescriptionsHandlePromptResponse(
    response: FSPrompt.Response<P>,
    /** Attached to every sync a response starts, so the presenter's own sync events reach it. */
    presenterContext: Map<String, Any>,
    emit: suspend E.() -> Unit,
) {
    when (val prompt = response.prompt) {
        is Prompt.UploadLimitReached -> {
            eventFactory.createPromptChange(promptFactory.none()).emit()
            if (prompt.selectionOf(response.selected) == Prompt.UploadLimitReached.Selection.ViewPlans) {
                eventFactory.createNavigation(WMNavigationRequest.Route(WMRoutes.PRICING)).emit()
            }
        }
        is Prompt.ConfirmDeleteUpload -> {
            eventFactory.createPromptChange(promptFactory.none()).emit()
            val uid = dataGateway.session()?.uid
            if (prompt.selectionOf(response.selected) == Prompt.ConfirmDeleteUpload.Selection.Delete && uid != null) {
                dataGateway.deleteUpload(uid, prompt.uploadId, presenterContext)
            }
        }
        is Prompt.DeleteUploadFailed -> eventFactory.createPromptChange(promptFactory.none()).emit()
        is Prompt.ConfirmReevaluate -> {
            val uid = dataGateway.session()?.uid
            if (prompt.selectionOf(response.selected) == Prompt.ConfirmReevaluate.Selection.Reevaluate && uid != null) {
                val id = Uuid.random().toString()
                dataGateway.upsertScreenParams(uid, WMJobDescriptionsPresenter.SCREEN, mapOf(WMJobDescriptionsPresenter.PARAM_ACTIVE_UPLOAD to id))
                eventFactory.createPromptChange(promptFactory.none(), loading = true).emit()
                FSLog.sendWMMixReevaluationRequested(mixId = prompt.mixId)
                dataGateway.reevaluateMix(uid, id, prompt.mixId, presenterContext)
            } else {
                eventFactory.createPromptChange(promptFactory.none()).emit()
            }
        }
        is Prompt.PreviewFile -> {
            eventFactory.createPromptChange(promptFactory.none()).emit()
            // The download link names the file as an attachment, so following it saves the file and keeps the page.
            if (prompt.selectionOf(response.selected) == Prompt.PreviewFile.Selection.Download) {
                eventFactory.createNavigation(WMNavigationRequest.External(prompt.file.downloadUrl)).emit()
            }
        }
        is Prompt.InvalidFile, is Prompt.InvalidUrl, is Prompt.UploadFailed, is Prompt.UploadNotAccepted,
        Prompt.FileUnavailable -> {
            eventFactory.createPromptChange(promptFactory.none()).emit()
        }
        else -> defaultHandlePromptResponse(response, emit)
    }
}

typealias WMDefaultJobDescriptionsPresenter = WMJobDescriptionsPresenter<Prompt.Base, Event.Base<Prompt.Base>, Action.Base>

fun WMJobDescriptionsPresenter.Companion.createDefault(
    dataGateway: WMJobDescriptionsDataGateway,
    instanceId: String = FSPresenter.createInstanceId("WMJobDescriptionsPresenter"),
    clock: Clock = Clock.System
): WMDefaultJobDescriptionsPresenter = object: WMBaseJobDescriptionsPresenter<
    Prompt.Base,
    Event.Base<Prompt.Base>,
    Action.Base,
    Event.Factory<Prompt.Base, Event.Base<Prompt.Base>>,
    Action.Factory<Prompt.Base, Action.Base>,
    Prompt.Factory<Prompt.Base>,
    WMJobDescriptionsDataGateway
>(
    dataGateway = dataGateway,
    eventFactory = Event.baseFactory(),
    actionFactory = Action.baseFactory(),
    promptFactory = Prompt.baseFactory(),
    cancelViewScopeOnUnready = true,
    instanceId = instanceId,
    clock = clock
) {}
